// Authored by Claude (Anthropic), working in Cowork with Matt Olden,
// Context Networks. Decisions, conventions and production data corrections
// are Matt's; the code is Claude's.
// Session: https://claude.ai/code/session_011hJXjKVFxCftbet72WMHQb
import React, { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { ioGet, ioSend } from './api'
import { btnPrimary, btnSecondary, cardClass, inputClass, statusBadge } from './ui'

// recordKind is an internal enum. Venues advertise their own venue on their own
// screens, so what they see is a campaign - never "house_message".
const KIND_LABELS = {
  house_message: 'Venue campaign',
  audience: 'Audience campaign',
  guaranteed: 'Guaranteed campaign',
}

const TABS = [
  { id: 'info', label: 'Basic Info' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'creatives', label: 'Creatives' },
  { id: 'copy', label: 'Copy Orders' },
  { id: 'po', label: 'Purchase Orders' },
  { id: 'notes', label: 'Notes' },
  { id: 'files', label: 'Files' },
  { id: 'account', label: 'Account' },
]

export default function CampaignRecord() {
  const { id } = useParams()
  const { isAdmin } = useAuth()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') || 'info'
  const [campaign, setCampaign] = useState(null)
  const [error, setError] = useState('')
  const [noteTitle, setNoteTitle] = useState('')
  const [noteBody, setNoteBody] = useState('')
  const [copyDue, setCopyDue] = useState('')
  const [copyStatus, setCopyStatus] = useState('pending')
  const [poNumber, setPoNumber] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    const data = await ioGet(`/api/io/campaigns/${id}`)
    setCampaign(data)
  }

  useEffect(() => {
    load().catch((e) => setError(e.message))
  }, [id])

  const setTab = (next) => {
    const n = new URLSearchParams(params)
    n.set('tab', next)
    setParams(n, { replace: true })
  }

  const patch = async (body) => {
    setBusy(true)
    setError('')
    try {
      const data = await ioSend(`/api/io/campaigns/${id}`, 'PUT', body)
      setCampaign(data)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const traffic = async () => {
    setBusy(true)
    try {
      await ioSend(`/api/io/campaigns/${id}/approve`, 'POST')
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!campaign && !error) return <p className="text-gray-500">Loading campaign…</p>
  if (!campaign) return <p className="text-red-600">{error}</p>

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <Link to="/campaigns" className="text-xs text-blue-600">← Campaigns</Link>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{campaign.campaignName}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={statusBadge(campaign.status)}>{campaign.status}</span>
            <span className="text-xs text-gray-500">{KIND_LABELS[campaign.recordKind] || 'Campaign'} · #{campaign.id}</span>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {campaign.recordKind === 'audience' && campaign.status === 'draft' && (
              <button type="button" className={btnSecondary} disabled={busy} onClick={() => patch({ convertProposal: true })}>
                Convert proposal
              </button>
            )}
            <button type="button" className={btnPrimary} disabled={busy} onClick={traffic}>Traffic</button>
            <button type="button" className={btnSecondary} disabled={busy} onClick={() => ioSend(`/api/io/campaigns/${id}/cancel`, 'POST').then(load)}>Drop</button>
            <button type="button" className={btnSecondary} disabled={busy} onClick={() => patch({ archive: !campaign.archivedAt })}>
              {campaign.archivedAt ? 'Unarchive' : 'Archive'}
            </button>
          </div>
        )}
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <div className="flex gap-4">
        <nav className="w-44 shrink-0 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                tab === t.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className={`${cardClass} flex-1 p-6 min-h-[24rem]`}>
          {tab === 'info' && (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Campaign" value={campaign.campaignName} />
              <Field label="Advertiser" value={campaign.advertiserName} />
              <Field label="Start" value={fmt(campaign.startDate)} />
              <Field label="End" value={fmt(campaign.endDate)} />
              <Field label="Contract / order" value={campaign.id} />
              <Field label="Booking reference" value={campaign.broadsign?.reservationId || 'Not booked'} />
              <Field label="Description" value={campaign.description || '—'} />
              <Field label="Goal" value={campaign.impressionGoal || (campaign.sharedGoals || []).map((g) => `${g.name}: ${g.target}`).join('; ') || '—'} />
            </dl>
          )}
          {tab === 'bookings' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Line items and booked faces (digital analog of Static Bookings).</p>
              {(campaign.lineItems || []).map((li, i) => (
                <div key={li.id || i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="font-medium text-gray-900 dark:text-white">{li.name || `Line ${i + 1}`}</div>
                  <div className="text-xs text-gray-500">{li.type} · {(li.displayUnitIds || []).length} face(s)</div>
                  <div className="text-xs text-gray-500 mt-1">{(li.displayUnitIds || []).join(', ') || 'No faces'}</div>
                </div>
              ))}
              {!(campaign.lineItems || []).length && (
                <p className="text-sm text-gray-500">
                  Faces: {(campaign.selectedDisplayUnitIds || []).join(', ') || 'None booked'}
                </p>
              )}
            </div>
          )}
          {tab === 'creatives' && (
            <ul className="space-y-2">
              {(campaign.files || []).map((f) => (
                <li key={f.id} className="text-sm">
                  <a className="text-blue-600" href={f.fileUrl} target="_blank" rel="noreferrer">{f.originalName}</a>
                  <span className="text-gray-500 text-xs ml-2">{f.specCheck || f.fileType}</span>
                  {f.contentId && <span className="text-xs text-gray-400 ml-2">content {f.contentId}</span>}
                </li>
              ))}
              {!(campaign.files || []).length && <p className="text-sm text-gray-500">No creatives uploaded.</p>}
            </ul>
          )}
          {tab === 'copy' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Creative delivery checklist for this campaign.</p>
              {(campaign.copyOrders || []).map((c, i) => (
                <div key={i} className="text-sm border-b border-gray-200 dark:border-gray-700 pb-2">
                  Due {c.dueDate || '—'} · {c.status}
                </div>
              ))}
              {isAdmin && (
                <div className="flex gap-2">
                  <input type="date" className={inputClass} value={copyDue} onChange={(e) => setCopyDue(e.target.value)} />
                  <select className={inputClass} value={copyStatus} onChange={(e) => setCopyStatus(e.target.value)}>
                    <option value="pending">pending</option>
                    <option value="received">received</option>
                    <option value="approved">approved</option>
                  </select>
                  <button type="button" className={btnPrimary} disabled={busy} onClick={() => patch({
                    copyOrders: [...(campaign.copyOrders || []), { dueDate: copyDue, status: copyStatus, createdAt: new Date().toISOString() }],
                  })}>Add</button>
                </div>
              )}
            </div>
          )}
          {tab === 'po' && (
            <div className="space-y-3">
              {(campaign.purchaseOrders || []).map((p, i) => (
                <div key={i} className="text-sm">PO {p.number} · {p.createdAt}</div>
              ))}
              {isAdmin && (
                <div className="flex gap-2">
                  <input className={inputClass} placeholder="PO number" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
                  <button type="button" className={btnPrimary} disabled={busy || !poNumber} onClick={() => patch({
                    purchaseOrders: [...(campaign.purchaseOrders || []), { number: poNumber, createdAt: new Date().toISOString() }],
                  })}>Add PO</button>
                </div>
              )}
            </div>
          )}
          {tab === 'notes' && (
            <div className="space-y-3">
              {(campaign.notes || []).map((n, i) => (
                <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="font-medium text-sm">{n.title}</div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{n.body}</p>
                </div>
              ))}
              <input className={inputClass} placeholder="Note title" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} />
              <textarea className={inputClass} rows={3} placeholder="Note" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} />
              <button type="button" className={btnPrimary} disabled={busy || !noteBody} onClick={() => {
                patch({ notes: [...(campaign.notes || []), { title: noteTitle, body: noteBody, at: new Date().toISOString() }] })
                setNoteTitle('')
                setNoteBody('')
              }}>Add note</button>
            </div>
          )}
          {tab === 'files' && (
            <ul className="text-sm space-y-2">
              {(campaign.files || []).map((f) => (
                <li key={f.id}><a className="text-blue-600" href={f.fileUrl} target="_blank" rel="noreferrer">{f.originalName}</a></li>
              ))}
              {!(campaign.files || []).length && <p className="text-gray-500">No files.</p>}
            </ul>
          )}
          {tab === 'account' && (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Advertiser" value={campaign.advertiserName} />
              <Field label="Contact email" value={campaign.contactEmail} />
              <Field label="Phone" value={campaign.contactPhone || '—'} />
              <Field label="Submitted by" value={campaign.userEmail || '—'} />
              <Field label="Venue" value={campaign.venueUid || '—'} />
              <Field
                label="Help Desk Ticket No."
                value={campaign.zammadTicketNumber || '—'}
                href={campaign.zammadTicketId ? `https://helpdesk.contextnetworks.net/#ticket/zoom/${campaign.zammadTicketId}` : null}
              />
            </dl>
          )}
        </div>
      </div>
    </div>
  )
}

function Field({ label, value, href }) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 dark:text-white">
        {href ? (
          <a className="text-blue-600 hover:underline" href={href} target="_blank" rel="noreferrer">
            {value || '—'}
          </a>
        ) : (
          value || '—'
        )}
      </dd>
    </div>
  )
}

function fmt(v) {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toISOString().slice(0, 10)
}
